import zhData from '@/data/i18n/json/zh'
import { GetValueType } from '@/data/i18n/translateLang'

/** 排版任务状态 */
export type PaibanTaskStatus =
  | 'running'
  | 'pending'
  | 'completed'
  | 'failed'
  | 'paused'
/** 排版任务操作 */
export type PaibanTaskAction =
  | 'planPreview'
  | 'startList'
  | 'pause'
  | 'remove'
  | 'continue'

/** 排版任务操作枚举 */
export enum PaibanTaskActionEnum {
  CONTINUE = 'continue',
  PAUSE = 'pause',
  DELETE = 'remove',
}

/** 状态映射 */
export const statusMap = {
  1: 'running', // 排版中
  2: 'pending', // 等待中
  3: 'paused', // 暂停中
  4: 'completed', // 已完成
  5: 'failed', // 失败
  6: 'removing', // 删除中
}

export enum PaibanTaskStatusEnum {
  PENDING = 2,
  RUNNING = 1,
  PAUSED = 3,
  COMPLETED = 4,
  FAILED = 5,
  DELETING = 6,
}

export interface PaibanTaskRow {
  id: string | number
  taskBatch: string
  relatedOrderNos: string
  taskStatus: PaibanTaskStatusEnum
  progress: number
  FGNo?: string | null
  boardCount?: string | number | null
  optimizeRate?: string | null
  optimizedArea?: string | null
  productionLine?: string | null
  failedReason?: string | null
  request_url?: string | null
  plank_url?: string | null
  resultUrlArr?: string[] | null
  finished_time?: string | null
  create_time?: string | null
  task_ids?: string[] | null
  started_time?: string | null
  queue_position?: number | null
  production_line_id?: number | null
  production_line_name?: string | null
}

export interface StatusOptionItem {
  label: GetValueType<typeof zhData>
  value: PaibanTaskStatusEnum | 0
}

export interface SendToPaibanTaskPayload {
  plank_url: string
  production_line_id: number
  order_nos: string
  pending_data_list: any[]
  production_from?: string
}

export interface FetchPaibanTaskListPayload {
  order_no?: string
  page: number
  status?: PaibanTaskStatus
  limit?: number
}

export interface UpdatePaibanTaskPayload {
  action: PaibanTaskActionEnum
  task: PaibanTaskRow
}

export const statusOptions: StatusOptionItem[] = [
  { label: '全部', value: 0 },
  { label: '等待中', value: PaibanTaskStatusEnum.PENDING },
  { label: '排版中', value: PaibanTaskStatusEnum.RUNNING },
  { label: '已完成', value: PaibanTaskStatusEnum.COMPLETED },
  { label: '任务失败', value: PaibanTaskStatusEnum.FAILED },
  { label: '暂停中', value: PaibanTaskStatusEnum.PAUSED },
]

export interface TableColumnProps {
  title: string
  /** 表头自定义渲染对应的 i18n key */
  titleI18nKey: string
  dataIndex?: string
  key: string
  width?: number
  ellipsis?: boolean
  scopedSlots?: { customRender?: string }
  fixed?: 'left' | 'right'
}

export const taskListColumns: TableColumnProps[] = [
  {
    title: '任务批次',
    titleI18nKey: 'paibanTask.taskBatchTitle',
    dataIndex: 'taskBatch',
    key: 'taskBatch',
    width: 130,
    ellipsis: true,
  },
  {
    title: '关联订单号',
    titleI18nKey: 'paibanTask.relatedOrderNosTitle',
    dataIndex: 'relatedOrderNos',
    key: 'relatedOrderNos',
    width: 160,
    ellipsis: true,
  },
  {
    title: '任务状态',
    titleI18nKey: 'paibanTask.taskStatus',
    dataIndex: 'taskStatus',
    key: 'taskStatus',
    scopedSlots: { customRender: 'taskStatus' },
    width: 200,
  },
  // {
  //   title: '排版进度',
  //   titleI18nKey: 'paibanTask.progressTitle',
  //   dataIndex: 'progress',
  //   key: 'progress',
  //   scopedSlots: { customRender: 'progressCol' },
  //   width: 160,
  // },
  {
    title: '大板用量估算',
    titleI18nKey: 'paibanTask.boardCountTitle',
    dataIndex: 'boardCount',
    key: 'boardCount',
    scopedSlots: { customRender: 'boardCount' },
    width: 120,
  },
  {
    title: '优化率估算',
    titleI18nKey: 'paibanTask.optimizeRateTitle',
    dataIndex: 'optimizeRate',
    key: 'optimizeRate',
    scopedSlots: { customRender: 'optimizeRate' },
    width: 120,
  },
  {
    title: '优化面积估算',
    titleI18nKey: 'paibanTask.optimizedAreaTitle',
    dataIndex: 'optimizedArea',
    key: 'optimizedArea',
    scopedSlots: { customRender: 'optimizedArea' },
    width: 120,
  },
  {
    title: '生产线',
    titleI18nKey: 'paibanTask.productionLineTitle',
    dataIndex: 'production_line_name',
    key: 'production_line_name',
    scopedSlots: { customRender: 'dashText' },
    width: 100,
    ellipsis: true,
  },
  {
    title: '操作',
    titleI18nKey: 'paibanTask.operationTitle',
    key: 'operation',
    scopedSlots: { customRender: 'operation' },
    width: 260,
    fixed: 'right',
  },
]

/** 模拟接口返回的完整任务列表 */
export const mockTaskListSource: PaibanTaskRow[] = [
  {
    id: 1,
    taskBatch: 'PB-20250520-001',
    relatedOrderNos: 'SO20250518001,SO20250518002',
    taskStatus: PaibanTaskStatusEnum.PENDING,
    progress: 0,
    boardCount: null,
    optimizeRate: null,
    optimizedArea: null,
    productionLine: null,
    failedReason: '',
  },
  {
    id: 2,
    taskBatch: 'PB-20250520-002',
    relatedOrderNos: 'SO20250519008',
    taskStatus: PaibanTaskStatusEnum.RUNNING,
    progress: 45,
    boardCount: '12',
    optimizeRate: '86.5%',
    optimizedArea: '128.6㎡',
    productionLine: '一号生产线',
    failedReason: null,
  },
  {
    id: 3,
    taskBatch: 'PB-20250519-003',
    relatedOrderNos: 'SO20250517003',
    taskStatus: PaibanTaskStatusEnum.RUNNING,
    progress: 72,
    boardCount: '8',
    optimizeRate: '91.2%',
    optimizedArea: '96.3㎡',
    productionLine: '二号生产线',
    failedReason: null,
  },
  {
    id: 4,
    taskBatch: 'PB-20250519-004',
    relatedOrderNos: 'SO20250516012,SO20250516015',
    taskStatus: PaibanTaskStatusEnum.COMPLETED,
    progress: 100,
    boardCount: '24',
    optimizeRate: '93.8%',
    optimizedArea: '312.5㎡',
    productionLine: '三号生产线',
    failedReason: null,
  },
  {
    id: 5,
    taskBatch: 'PB-20250518-005',
    relatedOrderNos: 'SO20250515006',
    taskStatus: PaibanTaskStatusEnum.COMPLETED,
    progress: 100,
    boardCount: '6',
    optimizeRate: '88.0%',
    optimizedArea: '54.2㎡',
    productionLine: '一号生产线',
    failedReason: null,
  },
  {
    id: 6,
    taskBatch: 'PB-20250518-006',
    relatedOrderNos: 'SO20250514021',
    taskStatus: PaibanTaskStatusEnum.FAILED,
    progress: 38,
    boardCount: '3',
    optimizeRate: null,
    optimizedArea: '28.1㎡',
    productionLine: '二号生产线',
    failedReason: '订单数据异常',
  },
  {
    id: 7,
    taskBatch: 'PB-20250517-007',
    relatedOrderNos: 'SO20250513009',
    taskStatus: PaibanTaskStatusEnum.PENDING,
    progress: 0,
    boardCount: null,
    optimizeRate: null,
    optimizedArea: null,
    productionLine: '备料线',
    failedReason: null,
  },
  {
    id: 8,
    taskBatch: 'PB-20250517-008',
    relatedOrderNos: 'SO20250512033,SO20250512035',
    taskStatus: PaibanTaskStatusEnum.COMPLETED,
    progress: 100,
    boardCount: '18',
    optimizeRate: '90.5%',
    optimizedArea: '201.8㎡',
    productionLine: '三号生产线',
    failedReason: null,
  },
  {
    id: 9,
    taskBatch: 'PB-20250516-009',
    relatedOrderNos: 'SO20250511002',
    taskStatus: PaibanTaskStatusEnum.FAILED,
    progress: 15,
    boardCount: null,
    optimizeRate: null,
    optimizedArea: null,
    productionLine: null,
    failedReason: '订单数据异常',
  },
  {
    id: 10,
    taskBatch: 'PB-20250516-010',
    relatedOrderNos: 'SO20250510018',
    taskStatus: PaibanTaskStatusEnum.PENDING,
    progress: 0,
    boardCount: null,
    optimizeRate: null,
    optimizedArea: null,
    productionLine: '一号生产线',
    failedReason: null,
  },
  {
    id: 11,
    taskBatch: 'PB-20250515-011',
    relatedOrderNos: 'SO20250509044',
    taskStatus: PaibanTaskStatusEnum.RUNNING,
    progress: 12,
    boardCount: '2',
    optimizeRate: '82.0%',
    optimizedArea: '18.5㎡',
    productionLine: '备料线',
    failedReason: null,
  },
  {
    id: 12,
    taskBatch: 'PB-20250515-012',
    relatedOrderNos: 'SO20250508007,SO20250508009',
    taskStatus: PaibanTaskStatusEnum.COMPLETED,
    progress: 100,
    boardCount: '15',
    optimizeRate: '92.1%',
    optimizedArea: '178.4㎡',
    productionLine: '二号生产线',
  },
]

/** 列表组件默认数据 */
export const taskList: PaibanTaskRow[] = mockTaskListSource
